interface RequestOptions {
    endpoint: string;
    method: string;
    data?: any;
    headers?: Record<string, string>;
    forceDirect?: boolean;
}

interface GetOptions {
    params?: Record<string, string>;
    headers?: Record<string, string>;
    forceDirect?: boolean;
}

interface MutationOptions {
    headers?: Record<string, string>;
    forceDirect?: boolean;
}

interface ProxyCacheData {
    available: boolean;
    expiry: number;
}

interface APIErrorPayload {
    message?: string;
    error?: string;
    code?: string;
    retry_after_seconds?: number;
    [key: string]: any;
}

export class APIRequestError extends Error {
    status: number;
    code?: string;
    retryAfterSeconds?: number;
    payload: APIErrorPayload;

    constructor(args: {
        message: string;
        status: number;
        code?: string;
        retryAfterSeconds?: number;
        payload?: APIErrorPayload;
    }) {
        super(args.message);
        this.name = 'APIRequestError';
        this.status = args.status;
        this.code = args.code;
        this.retryAfterSeconds = args.retryAfterSeconds;
        this.payload = args.payload ?? {};
    }
}

function parseRetryAfter(
    headerValue: string | null,
    payload?: APIErrorPayload
): number | undefined {
    const fromHeader = Number.parseInt(headerValue ?? '', 10);
    if (Number.isFinite(fromHeader) && fromHeader > 0) {
        return fromHeader;
    }

    const fromPayload = Number.parseInt(
        String(payload?.retry_after_seconds ?? ''),
        10
    );
    if (Number.isFinite(fromPayload) && fromPayload > 0) {
        return fromPayload;
    }

    return undefined;
}

async function parseResponsePayload(response: Response): Promise<any> {
    const text = await response.text().catch(() => '');
    if (!text) return null;

    try {
        return JSON.parse(text);
    } catch {
        return { error: text };
    }
}

function toErrorPayload(payload: any): APIErrorPayload {
    if (payload && typeof payload === 'object' && !Array.isArray(payload)) {
        return payload;
    }

    if (typeof payload === 'string') {
        return { error: payload };
    }

    return {};
}

function isProxyTimeoutError(error: APIRequestError): boolean {
    return (
        error.status === 504 ||
        /FUNCTION_INVOCATION_TIMEOUT|Request timeout/i.test(error.message)
    );
}

export class API {
    static proxyAvailable: boolean | null = null;
    static proxyCheckPromise: Promise<boolean> | null = null;
    static proxyCheckExpiry: number = 0;
    static PROXY_CHECK_TTL: number = 5 * 60 * 1000; // 5 minutes

    /**
     * Check if the editor proxy is available
     */
    static async checkProxyAvailable(): Promise<boolean> {
        const now = Date.now();

        // Return cached result if still valid
        if (API.proxyAvailable !== null && now < API.proxyCheckExpiry) {
            return API.proxyAvailable;
        }

        // Prevent multiple simultaneous checks
        if (API.proxyCheckPromise) {
            return API.proxyCheckPromise;
        }

        API.proxyCheckPromise = (async (): Promise<boolean> => {
            try {
                const response = await fetch('/api/editor-proxy', {
                    method: 'GET',
                });
                API.proxyAvailable = response.ok;
                API.proxyCheckExpiry = now + API.PROXY_CHECK_TTL;

                if (API.proxyAvailable) {
                    console.log(
                        'Editor proxy detected, using authenticated mode'
                    );
                } else {
                    console.log(
                        'No editor proxy found, using legacy direct mode'
                    );
                }

                // Store decision in sessionStorage with expiry
                if (typeof window !== 'undefined') {
                    sessionStorage.setItem(
                        'editor_proxy_available',
                        JSON.stringify({
                            available: API.proxyAvailable,
                            expiry: API.proxyCheckExpiry,
                        } as ProxyCacheData)
                    );
                }

                return API.proxyAvailable;
            } catch {
                API.proxyAvailable = false;
                API.proxyCheckExpiry = now + API.PROXY_CHECK_TTL;
                return false;
            } finally {
                API.proxyCheckPromise = null;
            }
        })();

        return API.proxyCheckPromise;
    }

    /**
     * Makes a GET request to the editor API
     */
    static async get<T = any>(
        endpoint: string,
        options: GetOptions = {}
    ): Promise<T | void> {
        const { params = {}, headers = {}, forceDirect } = options;
        const query = new URLSearchParams(params).toString();

        return await API.request<T>({
            endpoint: `${endpoint}?${query}`,
            method: 'GET',
            headers,
            forceDirect,
        });
    }

    /**
     * Makes a POST request to the editor API
     */
    static async post<T = any>(
        endpoint: string,
        data?: any,
        options: MutationOptions = {}
    ): Promise<T | void> {
        return await API.request<T>({
            endpoint,
            method: 'POST',
            data,
            ...options,
        });
    }

    /**
     * Makes a PATCH request to the editor API
     */
    static async patch<T = any>(
        endpoint: string,
        data?: any,
        options: MutationOptions = {}
    ): Promise<T | void> {
        return await API.request<T>({
            endpoint,
            method: 'PATCH',
            data,
            ...options,
        });
    }

    /**
     * Makes a DELETE request to the editor API
     */
    static async delete<T = any>(
        endpoint: string,
        data?: any,
        options: MutationOptions = {}
    ): Promise<T | void> {
        return await API.request<T>({
            endpoint,
            method: 'DELETE',
            data,
            ...options,
        });
    }

    /**
     * Legacy request method for direct API calls
     */
    static async legacyRequest<T = any>(
        options: RequestOptions
    ): Promise<T | void> {
        const { endpoint, method, data, headers = {} } = options;
        const base_url =
            process.env.NEXT_PUBLIC_LOCAL_REQUESTS === 'true'
                ? 'http://localhost:8080'
                : 'https://sites.nicejob.com';

        const url = `${base_url}/${endpoint.replace(/^\//, '')}`;

        const response = await fetch(url, {
            method: method,
            headers: {
                'Content-Type': 'application/json',
                ...headers,
            },
            credentials: 'include',
            mode: 'cors',
            body: data ? JSON.stringify(data) : null,
        });

        if (response.status === 204) {
            return;
        }

        const result = await parseResponsePayload(response);

        if (!response.ok) {
            const payload = toErrorPayload(result);
            throw new APIRequestError({
                message:
                    payload.message || payload.error || 'API request failed',
                status: response.status,
                code: payload.code,
                retryAfterSeconds: parseRetryAfter(
                    response.headers.get('retry-after'),
                    payload
                ),
                payload,
            });
        }

        return result;
    }

    /**
     * Proxy request method for authenticated API calls
     */
    static async proxyRequest<T = any>(
        options: RequestOptions
    ): Promise<T | void> {
        const { endpoint, method, data, headers = {} } = options;
        const base_url =
            process.env.NEXT_PUBLIC_LOCAL_REQUESTS === 'true'
                ? 'http://localhost:8080'
                : 'https://sites.nicejob.com';

        const url = `${base_url}/${endpoint.replace(/^\//, '')}`;

        const response = await fetch('/api/editor-proxy', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                method,
                url,
                headers,
                body: data,
            }),
        });

        if (response.status === 204) {
            return;
        }

        const result = await parseResponsePayload(response);

        if (!response.ok) {
            const payload = toErrorPayload(result);
            throw new APIRequestError({
                message:
                    payload.error || payload.message || 'Proxy request failed',
                status: response.status,
                code: payload.code,
                retryAfterSeconds: parseRetryAfter(
                    response.headers.get('retry-after'),
                    payload
                ),
                payload,
            });
        }

        return result;
    }

    /**
     * Main request method that decides between proxy and direct calls
     */
    static async request<T = any>(options: RequestOptions): Promise<T | void> {
        const { endpoint, method, data, headers = {}, forceDirect } = options;

        if (forceDirect) {
            return await API.legacyRequest<T>({
                endpoint,
                method,
                data,
                headers,
            });
        }

        // Check if proxy is available
        const useProxy = await API.checkProxyAvailable();

        if (useProxy) {
            try {
                // Use authenticated proxy
                return await API.proxyRequest<T>({
                    endpoint,
                    method,
                    data,
                    headers,
                });
            } catch (error) {
                if (error instanceof APIRequestError) {
                    if (error.status >= 500 && !isProxyTimeoutError(error)) {
                        console.warn(
                            'Proxy returned server error, falling back to direct call:',
                            error
                        );
                        API.resetProxyCheck();
                        return await API.legacyRequest<T>({
                            endpoint,
                            method,
                            data,
                            headers,
                        });
                    }
                    throw error;
                }

                // If proxy fails, retry once with direct call
                console.warn(
                    'Proxy request failed, falling back to direct call:',
                    error
                );
                API.resetProxyCheck(); // Reset proxy availability
                return await API.legacyRequest<T>({
                    endpoint,
                    method,
                    data,
                    headers,
                });
            }
        } else {
            // Use legacy direct call
            return API.legacyRequest<T>({ endpoint, method, data, headers });
        }
    }

    /**
     * Force refresh proxy availability check
     * Useful after deployment changes
     */
    static resetProxyCheck(): void {
        API.proxyAvailable = null;
        API.proxyCheckPromise = null;
        API.proxyCheckExpiry = 0;
        if (typeof window !== 'undefined') {
            sessionStorage.removeItem('editor_proxy_available');
        }
    }
}

// Initialize proxy check from sessionStorage if available
if (
    typeof window !== 'undefined' &&
    sessionStorage.getItem('editor_proxy_available')
) {
    try {
        const cached = JSON.parse(
            sessionStorage.getItem('editor_proxy_available')
        ) as ProxyCacheData;
        if (cached.expiry > Date.now()) {
            API.proxyAvailable = cached.available;
            API.proxyCheckExpiry = cached.expiry;
        }
    } catch {
        // Ignore invalid cached data
        sessionStorage.removeItem('editor_proxy_available');
    }
}
